import { FC, useState } from 'react';
import Modal from '../../components/shared/Modal/Modal';
import Field from '../../components/shared/Field/Field';
import { ServiceSupplier } from '../../services/service-supplier';
import { NotificationManager } from 'react-notifications';
import { useTranslation } from 'react-i18next';
import { skippedIdsOf, unavailableSkippedIdsOf, SupplierBulkRunOutcome } from '../../util/supplierBulkOutcome';

type Props = {
  show: boolean;
  setShow: (value: boolean) => void;
  supplierIds: string[];
  onSave: (outcome: SupplierBulkRunOutcome) => void;
  mode: 'block' | 'unblock';
  formatSupplierNames?: (ids: string[]) => string | null;
};

const BulkBlockSuppliersModal: FC<Props> = ({ show, setShow, supplierIds, onSave, mode, formatSupplierNames }) => {
  const [t] = useTranslation('supplierDatabase', { keyPrefix: `bulkActions.${mode}` });
  const [reason, setReason] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    if (!reason.trim()) return;
    setIsSaving(true);
    try {
      const serviceCall = mode === 'block' ? ServiceSupplier.bulkBlock : ServiceSupplier.bulkUnblock;
      const result = await serviceCall(supplierIds, reason);
      const changed = result.updated ?? 0;
      const skippedIds = skippedIdsOf(result);
      if (changed > 0) {
        NotificationManager.success(
          t('notification.success', '{{count}} leverantörer uppdaterade', { count: changed }),
          t('notification.title', 'Sparat')
        );
      }
      if (skippedIds.length > 0) {
        const names = formatSupplierNames?.(skippedIds) ?? null;
        const skippedText = mode === 'block'
          ? (names
            ? t('notification.skipped.withNames', {
              count: skippedIds.length,
              names,
              defaultValue_one: '{{count}} leverantör hoppades över (redan spärrad eller borttagen): {{names}}',
              defaultValue_other: '{{count}} leverantörer hoppades över (redan spärrade eller borttagna): {{names}}',
            })
            : t('notification.skipped.countOnly', {
              count: skippedIds.length,
              defaultValue_one: '{{count}} leverantör hoppades över (redan spärrad eller borttagen)',
              defaultValue_other: '{{count}} leverantörer hoppades över (redan spärrade eller borttagna)',
            }))
          : (names
            ? t('notification.skipped.withNames', {
              count: skippedIds.length,
              names,
              defaultValue_one: '{{count}} leverantör hoppades över (inte spärrad eller borttagen): {{names}}',
              defaultValue_other: '{{count}} leverantörer hoppades över (inte spärrade eller borttagna): {{names}}',
            })
            : t('notification.skipped.countOnly', {
              count: skippedIds.length,
              defaultValue_one: '{{count}} leverantör hoppades över (inte spärrad eller borttagen)',
              defaultValue_other: '{{count}} leverantörer hoppades över (inte spärrade eller borttagna)',
            }));
        NotificationManager.info(skippedText);
      }
      if (changed === 0 && skippedIds.length === 0) {
        NotificationManager.info(t('notification.nothingChanged', 'Inga ändringar gjordes.'));
      }
      setReason('');
      setShow(false);
      onSave({ changed, skippedIds, unavailableSkippedIds: unavailableSkippedIdsOf(result), retainSkipped: true, reclassify: mode });
    } catch (e) {
      const errorKey = (e as any)?.response?.data?.error;
      const errorMsg = errorKey === 'error.bulkWriteFailed'
        ? t('notification.partialWrite', 'Något gick fel och vissa leverantörer kan ha uppdaterats. Ladda om sidan för att se vad som hände.')
        : errorKey ?? t('notification.error', 'Fel');
      NotificationManager.error(errorMsg, t('notification.error', 'Fel'));
    }
    setIsSaving(false);
  };

  return (
    <Modal
      show={show}
      setShow={(v) => { setReason(''); setShow(v); }}
      title={mode === 'block'
        ? t('title', 'Spärra {{count}} leverantörer', { count: supplierIds.length })
        : t('title', 'Ta bort spärr för {{count}} leverantörer', { count: supplierIds.length })}
      buttonInfo={{
        label: mode === 'block' ? t('button', 'Spärra') : t('button', 'Ta bort spärr'),
        action: handleSave,
        loaderShow: isSaving,
        disabled: !reason.trim(),
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', fontSize: '14px' }}>
        <p style={{ color: 'var(--gray-700)' }}>
          {mode === 'block'
            ? t('description', 'Hindra de valda leverantörerna från att väljas i projekt.')
            : t('description', 'Tillåt de valda leverantörerna att väljas i projekt igen.')}
        </p>
        <Field label={t('reasonLabel', 'Skriv anledning')} value={reason} onChange={setReason} />
      </div>
    </Modal>
  );
};

export default BulkBlockSuppliersModal;
